import React from 'react';
import {
  TableHead as MUITableHead,
  TableRow,
  TableCell,
  Checkbox,
  TableSortLabel,
  Box,
  useTheme,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { visuallyHidden } from '@mui/utils';
import { HeadCell, TableHeadProps } from './playerTable.interface';
import { AccountTypes } from '../../../components/changeUser/accountTypes.enum';

export function PlayerTableHead({
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
  accountType,
  allowPlayerSelection,
}: TableHeadProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const isLarge = useMediaQuery(theme.breakpoints.down('lg'));
  const headCells: HeadCell[] = [
    {
      id: 'playerName',
      numeric: false,
      disablePadding: true,
      label: t('PLAYER_NAME'),
    },
    {
      id: 'team',
      numeric: true,
      disablePadding: false,
      label: t('TEAM'),
    },
    {
      id: 'rawDOB',
      numeric: true,
      disablePadding: false,
      label: t('DOB'),
    },
    {
      id: 'clearStatus',
      numeric: true,
      disablePadding: false,
      label: t('CLEAR_STATUS'),
    },
    {
      id: 'conneqtedStatus',
      numeric: true,
      disablePadding: false,
      label: t('CONNEQTED_STATUS'),
    },
    {
      id: 'guardianEmail',
      numeric: true,
      disablePadding: false,
      label: t('GUARDIAN_EMAIL'),
    },
  ];

  const sticky = {
    position: 'sticky',
    background:
      isSmall || isMedium || isLarge ? theme.palette.primary.contrastText : '',
    left: 0,
    zIndex: 100,
    width: '150px',
    paddingLeft: '15px',
  };

  return (
    <MUITableHead>
      <TableRow>
        {allowPlayerSelection && (
          <TableCell padding="none" sx={{ width: '50px' }}>
            <Checkbox
              sx={{ color: theme.palette.text.primary }}
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': t('SELECT_ALL_PLAYERS'),
              }}
            />
          </TableCell>
        )}
        {headCells
          .filter((cell) => {
            if (accountType !== AccountTypes.Org) {
              return cell.id !== 'guardianEmail' && cell.id !== 'team';
            }
            return cell;
          })
          .map((headCell) => (
            <TableCell
              key={headCell.id}
              align={headCell.numeric ? 'center' : 'left'}
              padding="none"
              sortDirection={orderBy === headCell.id ? order : false}
              sx={
                headCell.id === 'playerName'
                  ? sticky
                  : {
                      width: '150px',
                    }
              }
            >
              <TableSortLabel
                sx={{
                  '&.MuiTableSortLabel-root:hover': {
                    color: theme.palette.text.primary,
                  },
                  '&.Mui-active': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiTableSortLabel-icon': {
                    color: `${theme.palette.text.primary} !important`,
                  },
                }}
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={() => {
                  onRequestSort(headCell.id);
                }}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box
                    component="span"
                    sx={visuallyHidden}
                    data-testid="sortedBy"
                  >
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
        <TableCell sx={{ width: '70px' }} />
      </TableRow>
    </MUITableHead>
  );
}
